import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";

/**
 * Fundo "embaixo d'agua" do sistema, bem discreto, atras das paginas:
 *  - caustica: a luz do sol desenhando linhas tremidas no fundo do mar;
 *  - raios de luz entrando pela superficie;
 *  - bolhas subindo devagar.
 * So transform/opacity (roda na GPU) e nada recebe clique.
 */

const causticaSvg = (cor) =>
  `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='320' height='160' viewBox='0 0 320 160'>` +
      `<g fill='none' stroke='${cor}' stroke-width='2' stroke-linecap='round'>` +
      `<path d='M-10 40 C 30 10, 60 70, 100 40 S 170 10, 210 45 S 280 75, 330 40'/>` +
      `<path d='M-10 110 C 40 80, 70 140, 120 110 S 190 80, 230 115 S 290 140, 330 105'/>` +
      `<path d='M40 0 C 20 30, 70 60, 50 90 S 30 140, 60 170'/>` +
      `<path d='M200 -10 C 230 20, 180 60, 215 90 S 250 140, 220 170'/>` +
      `</g></svg>`
  )}")`;

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  const linha = escuro ? "rgba(45,212,191,0.07)" : "rgba(20,184,166,0.09)";
  const linha2 = escuro ? "rgba(94,234,212,0.05)" : "rgba(13,148,136,0.06)";
  return {
    mar: {
      position: "absolute",
      inset: 0,
      overflow: "hidden",
      pointerEvents: "none",
      zIndex: 0,
    },
    caustica: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "calc(100% + 640px)",
      height: "calc(100% + 320px)",
      backgroundImage: causticaSvg(linha),
      backgroundSize: "320px 160px",
      animation: "iaCaustica 60s linear infinite",
      willChange: "transform",
    },
    caustica2: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "calc(100% + 640px)",
      height: "calc(100% + 320px)",
      backgroundImage: causticaSvg(linha2),
      backgroundSize: "260px 130px",
      animation: "iaCausticaVolta 45s linear infinite",
      willChange: "transform",
    },
    // so o terco de cima tem caustica forte: some aos poucos para baixo
    mascara: {
      position: "absolute",
      inset: 0,
      WebkitMaskImage: "linear-gradient(180deg, #000 0%, rgba(0,0,0,.55) 45%, transparent 85%)",
      maskImage: "linear-gradient(180deg, #000 0%, rgba(0,0,0,.55) 45%, transparent 85%)",
    },
    raio: {
      position: "absolute",
      top: -60,
      width: 120,
      height: "75%",
      transformOrigin: "top center",
      background: escuro
        ? "linear-gradient(180deg, rgba(94,234,212,0.06), transparent 80%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.75), rgba(204,251,241,0.25) 40%, transparent 80%)",
      filter: "blur(6px)",
      animation: "iaRaio 11s ease-in-out infinite",
    },
    bolha: {
      position: "absolute",
      bottom: -20,
      borderRadius: "50%",
      border: `1px solid ${escuro ? "rgba(94,234,212,0.35)" : "rgba(13,148,136,0.28)"}`,
      background: escuro
        ? "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.25), rgba(45,212,191,0.06) 60%)"
        : "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95), rgba(20,184,166,0.08) 60%)",
      animation: "iaBolhaSobe linear infinite",
      willChange: "transform, opacity",
    },
  };
});

const MarDeFundo = () => {
  const classes = useStyles();
  // posicoes fixas por sessao (nada de pular a cada render)
  const bolhas = useMemo(
    () =>
      Array.from({ length: 12 }).map((_, i) => ({
        left: `${4 + ((i * 37) % 92)}%`,
        size: 5 + ((i * 7) % 10),
        dur: 16 + ((i * 5) % 14),
        delay: -((i * 3.7) % 20),
      })),
    []
  );
  const raios = [
    { left: "18%", giro: "12deg", delay: "0s" },
    { left: "42%", giro: "6deg", delay: "-4s" },
    { left: "66%", giro: "16deg", delay: "-7s" },
    { left: "84%", giro: "9deg", delay: "-2s" },
  ];

  return (
    <div className={classes.mar} aria-hidden="true">
      <div className={classes.mascara}>
        <div className={classes.caustica} />
        <div className={classes.caustica2} />
      </div>
      {raios.map((r) => (
        <span
          key={r.left}
          className={classes.raio}
          style={{ left: r.left, "--giro": r.giro, animationDelay: r.delay }}
        />
      ))}
      {bolhas.map((b, i) => (
        <span
          key={i}
          className={classes.bolha}
          style={{
            left: b.left,
            width: b.size,
            height: b.size,
            animationDuration: `${b.dur}s`,
            animationDelay: `${b.delay}s`,
          }}
        />
      ))}
    </div>
  );
};

export default MarDeFundo;
